import { isProductEditableByUser } from 'common/modules/products/utils';
import { isKeycardProduct, isLiftTicketProduct } from 'common/modules/skidata';
import { getTranslation, Languages } from 'common/modules/translations';
import {
	AccessClaims,
	ById,
	Category,
	OpeningHoursWithExceptions,
	PricingTableObject,
	ProductApi,
} from 'common/types';
import { getLowestPriceFromStockProduct } from 'common/utils/pricing';
import { getProductImageSrc } from 'common/utils/productUtils';
import { TagProps } from 'components/Tag';
import { TFunction } from 'react-i18next';
import { getProductTags } from 'services/products';

export interface ProductsTableRow {
	id: string;
	name: string;
	image: string | undefined;
	price: number | null;
	deposit: number;
	status: 'hidden' | 'public';
	tags: TagProps[];
	categories: string;
	channels: string;
	created: string;
	canEdit: boolean;
	isNotKeycard: boolean;
	isNotSkidataProduct: boolean;
}

export const productToProductsTableRow = (deps: {
	t: TFunction;
	lang: Languages;
	pricingTables: PricingTableObject;
	openingHours: OpeningHoursWithExceptions;
	shopLocationCount: number;
	categoriesById: ById<Category>;
	userAccess: AccessClaims | null;
}) => (product: ProductApi): ProductsTableRow => {
	const {
		t,
		lang,
		pricingTables,
		openingHours,
		categoriesById,
		userAccess,
		shopLocationCount,
	} = deps;

	const isLiftTicket = isLiftTicketProduct(product);
	const isKeycard = isKeycardProduct(product);

	const canEdit = isProductEditableByUser(product, userAccess);
	const isNotSkidataProduct = !isLiftTicket && !isKeycard;

	return {
		id: product.id,
		name: getTranslation(product.name, lang),
		image: getProductImageSrc(product),
		price: getLowestPriceFromStockProduct({
			product,
			pricingTables,
			openingHoursWithExceptions: openingHours,
			startDate: null,
			channel: 'ALL',
		}),
		deposit: product.terms?.deposit || 0,
		status: product.additionalProduct ? 'hidden' : 'public',
		tags: getProductTags(product, shopLocationCount, t),
		categories: getProductCategories(product, categoriesById, lang),
		channels: getProductChannels(product, t),
		created: product.created,
		canEdit,
		isNotSkidataProduct,
		isNotKeycard: !isKeycard,
	};
};

const getProductCategories = (
	product: ProductApi,
	categoriesById: ById<Category>,
	lang: Languages,
): string => {
	return (
		product.categoryIds
			?.map((id) => {
				const category = categoriesById[id];
				return !!category ? getTranslation(category.name, lang) : '';
			})
			.filter((name) => !!name)
			.sort()
			.join(', ') ?? ''
	);
};

const getProductChannels = (product: ProductApi, t: TFunction): string => {
	const { hiddenFromChannels = {} } = product;
	const allChannels = ['ONLINE', 'ADMIN', 'STORE'];
	const localeMap = {
		ONLINE: t('common:salesChannel.onlineShort', 'Online'),
		STORE: t('common:salesChannel.check-in', 'Check-in'),
		ADMIN: t('common:salesChannel.adminDashboardShort', 'Admin'),
	};
	const visibleChannels = allChannels.filter((c) => !hiddenFromChannels[c]);

	return visibleChannels.map((c) => localeMap[c]).join(', ');
};
